import { useState, useEffect } from "react";
import {
  Dialog,
  Box,
  Typography,
  TextField,
  Grid,
  Card,
  Button,
  Stack,
  IconButton,
  Select,
  MenuItem,
  alpha,
  useTheme,
  Slide,
  LinearProgress,
} from "@mui/material";
import { X, Users, CheckCircle2 } from "lucide-react";
import {
  teamCapabilities,
  capabilityCategories,
} from "../../config/teamCapabilities";
import { getAllUsers } from "../../api/users.api";
import { forwardRef } from "react";

const SlideTransition = forwardRef(function SlideTransition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function AddTeamModal({ open, onClose, onSubmit, editTeam }) {
  const theme = useTheme();
  const isEditing = Boolean(editTeam);

  const [form, setForm] = useState({
    name: "",
    description: "",
    capabilities: [],
    lead: "",
  });
  const [usersList, setUsersList] = useState([]);

  useEffect(() => {
    if (open) {
      const fetchUsers = async () => {
        try {
          const res = await getAllUsers();
          setUsersList(res?.data?.data || []);
        } catch (err) {
          console.error("Failed to load users:", err);
        }
      };
      fetchUsers();
    }
  }, [open]);

  useEffect(() => {
    if (editTeam && open) {
      setForm({
        name: editTeam.name || "",
        description: editTeam.description || "",
        capabilities: editTeam.capabilities || [],
        lead: editTeam.lead || "",
      });
    } else if (!open) {
      setForm({ name: "", description: "", capabilities: [], lead: "" });
    }
  }, [editTeam, open]);

  const toggleCapability = (id) => {
    setForm((prev) => ({
      ...prev,
      capabilities: prev.capabilities.includes(id)
        ? prev.capabilities.filter((c) => c !== id)
        : [...prev.capabilities, id],
    }));
  };

  const handleSubmit = () => {
    const { lead, ...payload } = form;
    if (isEditing) {
      onSubmit?.(editTeam._id, payload);
    } else {
      onSubmit?.(payload);
    }
    setForm({ name: "", description: "", capabilities: [], lead: "" });
    onClose();
  };

  const totalCaps = teamCapabilities.length;
  const selectedCount = form.capabilities.length;
  const progressPercent = (selectedCount / totalCaps) * 100;

  const labelSx = {
    fontWeight: 700,
    fontSize: 11,
    mb: 0.75,
    color: "#6B7280",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      bgcolor: "#F9FAFB",
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      TransitionComponent={SlideTransition}
      TransitionProps={{ timeout: 350 }}
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: "0 25px 60px -12px rgba(0, 0, 0, 0.2)",
          maxHeight: "90vh",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          minHeight: { xs: "auto", sm: 520 },
          "@keyframes fadeInScale": {
            from: { opacity: 0, transform: "scale(0.96)" },
            to: { opacity: 1, transform: "scale(1)" },
          },
        }}
      >
        {/* ─── Left Gradient Panel ─── */}
        <Box
          sx={{
            display: { xs: "none", sm: "flex" },
            flexDirection: "column",
            justifyContent: "space-between",
            width: 260,
            minWidth: 260,
            background: `linear-gradient(180deg, ${theme.sidebar.bgStart} 0%, ${theme.sidebar.bgEnd} 100%)`,
            p: 4,
            animation: "fadeInScale 0.4s ease forwards",
          }}
        >
          {/* Top Content */}
          <Box>
            {/* Team Icon */}
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 3,
                bgcolor: "rgba(255,255,255,0.15)",
                border: "1.5px solid rgba(255,255,255,0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 3,
                transition: "transform 0.3s ease",
                "&:hover": { transform: "scale(1.05)" },
              }}
            >
              <Users size={26} color="#fff" strokeWidth={1.8} />
            </Box>

            {/* Title */}
            <Typography
              sx={{
                color: "#fff",
                fontWeight: 800,
                fontSize: 22,
                mb: 1.5,
                lineHeight: 1.2,
              }}
            >
              {isEditing ? "Update Team" : "Create Team"}
            </Typography>

            {/* Description */}
            <Typography
              sx={{
                color: "rgba(255,255,255,0.6)",
                fontSize: 13,
                lineHeight: 1.7,
              }}
            >
              {isEditing
                ? "Update your team's identity and capabilities."
                : "Define your team's core identity and their boundary of operations within the ecosystem."}
            </Typography>
          </Box>

          {/* Bottom: Selection Summary */}
          <Box
            sx={{
              bgcolor: "rgba(255,255,255,0.08)",
              borderRadius: 3,
              p: 2.5,
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <Typography
              sx={{
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.45)",
                mb: 1.5,
              }}
            >
              Selection Summary
            </Typography>

            <Stack
              direction="row"
              alignItems="baseline"
              spacing={0.5}
              mb={1.5}
            >
              <Typography
                sx={{
                  fontSize: 32,
                  fontWeight: 800,
                  color: "#fff",
                  lineHeight: 1,
                }}
              >
                {selectedCount}
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  color: "rgba(255,255,255,0.5)",
                  fontWeight: 500,
                }}
              >
                of {totalCaps}
              </Typography>
            </Stack>

            <LinearProgress
              variant="determinate"
              value={progressPercent}
              sx={{
                height: 4,
                borderRadius: 2,
                bgcolor: "rgba(255,255,255,0.12)",
                mb: 2,
                "& .MuiLinearProgress-bar": {
                  borderRadius: 2,
                  bgcolor: "#fff",
                },
              }}
            />

            <Typography
              sx={{
                fontSize: 11,
                color: "rgba(255,255,255,0.35)",
                fontStyle: "italic",
                lineHeight: 1.6,
              }}
            >
              "Good permissions are as much about protection as they are
              about access."
            </Typography>
          </Box>
        </Box>

        {/* ─── Right Form Panel ─── */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            bgcolor: "#fff",
            minWidth: 0,
            width: 0,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              px: { xs: 2.5, sm: 3.5 },
              pt: 3,
              pb: 1,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: 18, sm: 20 },
                  color: "#111827",
                  mb: 0.25,
                }}
              >
                Team Identity
              </Typography>
            </Box>

            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                mt: 0.5,
                color: "#9CA3AF",
                transition: "all 0.2s ease",
                "&:hover": {
                  color: "#374151",
                  transform: "rotate(90deg)",
                },
              }}
            >
              <X size={18} />
            </IconButton>
          </Box>

          {/* Scrollable Form Content */}
          <Box
            sx={{
              flex: 1,
              px: { xs: 2.5, sm: 3.5 },
              py: 2,
              overflowY: "auto",
              "&::-webkit-scrollbar": { width: 6 },
              "&::-webkit-scrollbar-track": {
                bgcolor: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: alpha(theme.palette.primary.main, 0.2),
                borderRadius: 3,
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.35),
                },
              },
            }}
          >
            {/* Team Name + Team Lead Row */}
            <Grid container spacing={2} mb={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography sx={labelSx}>Team Name</Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="e.g. Sales Executive"
                  value={form.name}
                  onChange={(e) =>
                    setForm({ ...form, name: e.target.value })
                  }
                  sx={inputSx}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography sx={labelSx}>Team Lead</Typography>
                <Select
                  fullWidth
                  size="small"
                  value={form.lead}
                  onChange={(e) =>
                    setForm({ ...form, lead: e.target.value })
                  }
                  displayEmpty
                  sx={{
                    borderRadius: 2,
                    bgcolor: "#F9FAFB",
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                >
                  <MenuItem value="" disabled>
                    <em>Select a lead...</em>
                  </MenuItem>
                  {usersList.map((u) => (
                    <MenuItem key={u._id} value={u._id}>
                      {u.name}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
            </Grid>

            {/* Define Capabilities Header */}
            <Box mb={1}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 16,
                  color: "#111827",
                  mb: 0.5,
                }}
              >
                Define Capabilities
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  color: "#9CA3AF",
                  mb: 2.5,
                }}
              >
                Assign operational permissions to this team profile.
              </Typography>
            </Box>

            {/* Categorized Capabilities Grid */}
            {capabilityCategories.map((cat) => {
              const caps = teamCapabilities.filter(
                (c) => c.category === cat.id
              );

              return (
                <Box key={cat.id} mb={3}>
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.12em",
                      color: "#9CA3AF",
                      mb: 1.5,
                    }}
                  >
                    {cat.label}
                  </Typography>

                  <Grid container spacing={1.5}>
                    {caps.map((cap) => {
                      const Icon = cap.icon;
                      const active = form.capabilities.includes(
                        cap.id
                      );

                      return (
                        <Grid
                          size={{ xs: 6, sm: 3 }}
                          key={cap.id}
                        >
                          <Card
                            onClick={() => toggleCapability(cap.id)}
                            elevation={0}
                            sx={{
                              p: 1.5,
                              textAlign: "center",
                              cursor: "pointer",
                              borderRadius: 3,
                              border: active
                                ? `1.5px solid ${alpha(cap.color, 0.5)}`
                                : "1.5px solid #E5E7EB",
                              bgcolor: active
                                ? alpha(cap.color, 0.06)
                                : "#fff",
                              transition:
                                "all 0.2s cubic-bezier(0.23, 1, 0.32, 1)",
                              position: "relative",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              minHeight: 80,
                              "&:hover": {
                                borderColor: active
                                  ? alpha(cap.color, 0.6)
                                  : "#D1D5DB",
                                transform: "translateY(-2px)",
                                boxShadow: active
                                  ? `0 4px 12px ${alpha(cap.color, 0.15)}`
                                  : "0 2px 8px rgba(0,0,0,0.06)",
                              },
                            }}
                          >
                            {/* Checkmark badge */}
                            {active && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: 6,
                                  right: 6,
                                  "@keyframes popIn": {
                                    from: {
                                      opacity: 0,
                                      transform: "scale(0.5)",
                                    },
                                    to: {
                                      opacity: 1,
                                      transform: "scale(1)",
                                    },
                                  },
                                  animation:
                                    "popIn 0.2s ease forwards",
                                }}
                              >
                                <CheckCircle2
                                  size={16}
                                  color={cap.color}
                                  fill={alpha(cap.color, 0.15)}
                                />
                              </Box>
                            )}

                            {/* Icon */}
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: 2,
                                bgcolor: alpha(cap.color, 0.12),
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mb: 0.75,
                                transition: "all 0.2s ease",
                              }}
                            >
                              <Icon size={18} color={cap.color} />
                            </Box>

                            {/* Label */}
                            <Typography
                              sx={{
                                fontSize: 11,
                                fontWeight: 600,
                                color: active
                                  ? "#374151"
                                  : "#6B7280",
                                lineHeight: 1.2,
                              }}
                            >
                              {cap.shortLabel}
                            </Typography>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              );
            })}
          </Box>

          {/* Actions */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1.5,
              px: { xs: 2.5, sm: 3.5 },
              py: 2.5,
              borderTop: "1px solid #F3F4F6",
            }}
          >
            <Button
              onClick={onClose}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                px: 2.5,
                py: 0.75,
                fontSize: 13,
                fontWeight: 500,
                color: "#6B7280",
                transition: "all 0.2s ease",
                "&:hover": {
                  bgcolor: "#F3F4F6",
                },
              }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              disabled={!form.name || form.capabilities.length === 0}
              onClick={handleSubmit}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                px: 3,
                py: 1,
                fontSize: 13,
                fontWeight: 700,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.avatarGradients_1.avatarEnd} 100%)`,
                color: "#fff",
                boxShadow: `0 4px 14px ${theme.palette.primary.main}30`,
                transition:
                  "all 0.25s cubic-bezier(0.23, 1, 0.32, 1)",
                "&:hover": {
                  transform: "translateY(-1px)",
                  boxShadow: `0 6px 20px ${theme.palette.primary.main}40`,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.avatarGradients_1.avatarEnd} 100%)`,
                },
                "&:active": {
                  transform: "translateY(0)",
                },
                "&.Mui-disabled": {
                  background: "#E5E7EB",
                  color: "#9CA3AF",
                  boxShadow: "none",
                },
              }}
            >
              {isEditing ? "Update Team" : "Save Team"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}
